import React, { ReactNode, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import BootstrapModal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import { TextInputHookForm as TextInput } from '@common/components/form/inputs/text';
import { PrivateMessage } from '@modules/chat/types/objects';
import Linkify from 'react-linkify';
import { LoggedUser } from '@common/types/objects';
import { Icon } from '@common/components/icon';
import moment from 'moment';
import GraphemeSplitter from 'grapheme-splitter';

const { Footer } = BootstrapModal;

type Props = {
  children: ReactNode;
  className?: string;
  message: PrivateMessage;
  loggedUser: LoggedUser;
  shouldShow: boolean;
  onShow: () => void;
  onHide: () => void;
  onEdit: (message: PrivateMessage) => void;
};

const splitter = new GraphemeSplitter();
let regex: RegExp | undefined;
try {
  // eslint-disable-next-line prefer-regex-literals
  regex = new RegExp('\\p{Extended_Pictographic}', 'u');
} catch (err) {
  // Regex not supported
}

const LinkifyDecorator = (decoratedHref: string, decoratedText: string, key: number) => (
  <a target="blank" rel="noopener" className="linkified" href={decoratedHref} key={key}>
    {decoratedText}
  </a>
);

export const EditMessageModal = ({
  children,
  message,
  shouldShow,
  onShow,
  onHide,
  onEdit
}: Props) => {
  const { t } = useTranslation();
  const { control, formState, handleSubmit, reset } = useForm<PrivateMessage>({
    mode: 'all',
    defaultValues: message
  });

  const formValues = useWatch({ control }) as PrivateMessage;
  const timestamp = moment(message.created_at).format('HH:mm');

  const getSize = (size: number) => {
    switch (size) {
      case 1: return 36;
      case 2: return 32;
      case 3: return 24;
      default: return 14;
    }
  };

  const getFontSize = (text: string) => {
    const splitted = splitter.splitGraphemes(text); // Some icons contain of multiple unicode characters so we have to use a lib for this
    const shouldResize = regex && splitted && splitted.length <= 3 && splitted.every((c) => regex?.test(c)); // If length is 3 or below and they are all emojis
    return shouldResize ? getSize(splitted.length) : null;
  };

  const {
    textFontSize, textLineHeight
  } = useMemo(() => {
    const textSize = getFontSize(message.text) || undefined;
    return {
      textFontSize: textSize && `${textSize}px`,
      textLineHeight: textSize && `${textSize * 1.2}px`,
    };
  }, [message.text]);

  const trigger = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<{ onClick?: () => void }>, {
      onClick: () => onShow()
    })
    : children;

  const handleEdit = (data: PrivateMessage) => {
    onEdit(data);
    onHide();
    reset();
  };

  return (
    <>
      {trigger}
      <BootstrapModal
        key="modal"
        show={shouldShow}
        onHide={onHide}
        className="EditMessageModal"
      >
        <div className="EditMessageModal__Container">
          <div className="EditMessageModal__Header">
            <Icon type="close" onClick={onHide} />
            <h3 className="EditMessageModal__Title">{t('chat:edit_message')}</h3>
          </div>
          <form onSubmit={handleSubmit(handleEdit)}>
            <div className="tw-flex tw-flex-col">
              <div className="PrivateMessage">
                {formValues.text && (
                  <div className="PrivateMessage__Container">
                    <div className="PrivateMessage__Inner !tw-bg-chat-sender">
                      {formValues.text && (
                        <span className="Linkify">
                          <Linkify componentDecorator={LinkifyDecorator}>
                            <span style={{ fontSize: textFontSize, lineHeight: textLineHeight }}>
                              {formValues.text}
                            </span>
                            <small className="PrivateMessage__timestamp PrivateMessage__timestamp--hidden">
                              {timestamp}
                            </small>
                          </Linkify>
                        </span>
                      )}
                      <small className="PrivateMessage__timestamp">
                        {timestamp}
                      </small>
                    </div>
                  </div>
                )}
              </div>
              <Footer className="EditMessageModal__Footer">
                <TextInput
                  name="text"
                  control={control}
                />
                <Button
                  type="primary"
                  buttonType="submit"
                  icon="send"
                  isLoading={formState.isSubmitting}
                  disabled={!formValues.text || formState.isSubmitting || !formState.isDirty}
                />
              </Footer>
            </div>
          </form>
        </div>
      </BootstrapModal>
    </>
  );
};
